import { DataErrorApi } from './data-error-api';
import type {
  IrregularDeltaItem,
  IrregularDeltaApiItem,
  DeltaMeterValue,
  MeterValueApiItem,
  FacetOption,
  FiltersApiResponse,
  HandleActionRequest,
  HandleActionResponse,
  BatchHandleActionRequest,
  BatchHandleActionResponse,
  UpdateCommentRequest,
  IrregularDeltaStatus,
  HandleType,
  MachineUsageType,
  ReadingContext,
  DataErrorFilters,
} from '../types/data-error-types';

export interface GetIrregularDeltaResult {
  items: IrregularDeltaItem[];
  totalCount: number;
}

// Validate an API string against a known set of enum values; fall back to a safe default.
function safeEnum<T extends string>(value: string | null | undefined, valid: readonly T[], fallback: T): T {
  return value != null && (valid as readonly string[]).includes(value) ? (value as T) : fallback;
}

const VALID_STATUSES = ['Pending', 'Approved', 'Rejected', 'Deleted'] as const;
const VALID_HANDLE_TYPES = ['Approve', 'Replacement', 'Delete', 'ManualCorrection', 'Ignore'] as const;
const VALID_USAGE_TYPES = ['Public', 'SemiPublic', 'PrivateHouse', 'SharedBuilding'] as const;
const VALID_READING_CONTEXTS = [
  'SamplePeriodic', 'SampleClock', 'TransactionBegin', 'TransactionEnd',
  'InterruptionBegin', 'InterruptionEnd', 'Trigger', 'Other',
] as const;

export interface GetFiltersResult {
  statusOptions: FacetOption[];
  locationUsageOptions: FacetOption[];
}

export class DataErrorRepository {
  constructor(private readonly api: DataErrorApi) {}

  async getIrregularDelta(
    page: number,
    pageSize: number,
    filters: DataErrorFilters
  ): Promise<GetIrregularDeltaResult> {
    try {
      const filter = DataErrorApi.buildODataFilter(filters);
      const baseParams = { filter, search: filters.search, orderBy: 'irregularId desc' };

      // Two parallel requests: rows (count=false) + total count (count=true)
      const [rowsResult, countResult] = await Promise.all([
        this.api.getIrregularDeltaRows({
          ...baseParams,
          skip: page * pageSize,
          top: pageSize,
        }),
        this.api.getIrregularDeltaCount({ filter, search: filters.search }),
      ]);

      if (!rowsResult.isSuccess) {
        throw new Error(rowsResult.errorMessage ?? 'Failed to fetch irregular delta records');
      }

      return {
        items: (rowsResult.rows ?? []).map(mapIrregularDeltaItem),
        totalCount: countResult.count ?? 0,
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch irregular delta records';
      throw new Error(message);
    }
  }

  async getFilters(
    periodFrom: string | null,
    periodTo: string | null,
    search: string
  ): Promise<GetFiltersResult> {
    try {
      const filterParts: string[] = [];
      if (periodFrom) filterParts.push(`created ge ${periodFrom}T00:00:00Z`);
      if (periodTo) filterParts.push(`created le ${periodTo}T23:59:59Z`);

      const result: FiltersApiResponse = await this.api.getFilters({
        filter: filterParts.join(' and '),
        search,
      });

      if (!result.isSuccess) {
        throw new Error(result.errorMessage ?? 'Failed to fetch filters');
      }

      const statusOptions: FacetOption[] = [];
      const locationUsageOptions: FacetOption[] = [];

      for (const group of result.filters ?? []) {
        if (group.slug === 'handled') {
          for (const facet of group.facets ?? []) {
            if (facet.slug !== null) {
              statusOptions.push({
                key: facet.slug,
                label: facet.label ?? facet.slug,
                count: facet.count,
              });
            }
          }
        } else if (group.slug === 'usageType') {
          for (const facet of group.facets ?? []) {
            if (facet.slug !== null) {
              locationUsageOptions.push({
                key: facet.slug,
                label: facet.label ?? facet.slug,
                count: facet.count,
              });
            }
          }
        }
      }

      return { statusOptions, locationUsageOptions };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch filters';
      throw new Error(message);
    }
  }

  async exportExcel(filters: DataErrorFilters): Promise<Blob> {
    try {
      const filter = DataErrorApi.buildODataFilter(filters);
      return await this.api.exportExcel({
        skip: 0,
        top: 100000,
        orderBy: 'irregularId desc',
        filter,
        search: filters.search,
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to export';
      throw new Error(message);
    }
  }

  async getMeterValuesSurround(
    meterValueId: number,
    machineId: number,
    surroundSize: number
  ): Promise<DeltaMeterValue[]> {
    try {
      const result = await this.api.getMeterValuesSurround(meterValueId, machineId, surroundSize);

      if (!result.isSuccess) {
        throw new Error(result.errorMessage ?? 'Failed to fetch meter values');
      }

      return (result.rows ?? []).map(mapMeterValue);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch meter values';
      throw new Error(message);
    }
  }

  async handleAction(body: HandleActionRequest): Promise<HandleActionResponse> {
    try {
      return await this.api.handleAction(body);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to handle action';
      throw new Error(message);
    }
  }

  async batchHandleAction(body: BatchHandleActionRequest): Promise<BatchHandleActionResponse> {
    try {
      return await this.api.batchHandleAction(body);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to handle batch action';
      throw new Error(message);
    }
  }

  async updateComment(body: UpdateCommentRequest): Promise<IrregularDeltaItem | null> {
    try {
      const result = await this.api.updateComment(body);
      if (!result.isSuccess) {
        throw new Error(result.errorMessage ?? 'Failed to update comment');
      }
      return result.irregularDelta ? mapIrregularDeltaItem(result.irregularDelta) : null;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update comment';
      throw new Error(message);
    }
  }

  async rebuildPayments(operatorId: number): Promise<void> {
    await this.api.rebuildPayments(operatorId);
  }
}

// ── Mappers ───────────────────────────────────────────────────────────────────

function mapIrregularDeltaItem(api: IrregularDeltaApiItem): IrregularDeltaItem {
  return {
    irregularId: api.irregularId,
    machineId: api.machineId,
    machineName: api.machineName ?? '',
    connectorId: api.connectorId,
    physicalReference: api.physicalReference ?? null,
    operatorId: api.operatorId,
    actorId: api.actorId,
    actorName: api.actorName ?? null,
    sessionId: api.sessionId ?? null,
    usageType: (api.usageType && (VALID_USAGE_TYPES as readonly string[]).includes(api.usageType) ? api.usageType as MachineUsageType : null),
    createdAt: new Date(api.created),
    updatedAt: new Date(api.updated),
    durationSeconds: api.duration,
    fromEnergy: api.fromEnergy,
    toEnergy: api.toEnergy,
    delta: api.delta,
    fromMeterValueId: api.fromMeterValueId,
    toMeterValueId: api.toMeterValueId,
    fromMeterValueTimestamp: new Date(api.fromMeterValueTimeStamp),
    toMeterValueTimestamp: new Date(api.toMeterValueTimeStamp),
    handled: api.handled,
    statusId: safeEnum(api.statusId, VALID_STATUSES, 'Pending'),
    handleType: (api.handleType && (VALID_HANDLE_TYPES as readonly string[]).includes(api.handleType) ? api.handleType as HandleType : null),
    handledBy: api.handledBy ?? null,
    handledByName: api.handledByName ?? null,
    handledDate: api.handledDate ? new Date(api.handledDate) : null,
    comments: api.comments ?? '',
  };
}

function mapMeterValue(api: MeterValueApiItem): DeltaMeterValue {
  return {
    meterValueId: api.meterValueId,
    timestamp: new Date(api.timeStamp),
    machineTimestamp: new Date(api.machineTimeStamp),
    readingContext: safeEnum(api.readingContexts, VALID_READING_CONTEXTS, 'Other'),
    energy: api.energy ?? null,
    deltaEnergy: api.deltaEnergy ?? null,
    power: api.power ?? null,
    current: api.current ?? null,
    voltage: api.voltage ?? null,
    temperature: api.temperature ?? null,
    stateOfCharge: api.stateOfCharge ?? null,
    tariffName: api.taarifName ?? null, // fix intentional API typo here
    unitCost: api.unitCost ?? null,
    cost: api.cost ?? null,
    timeCost: api.timeCost ?? null,
    timeUnitCost: api.timeUnitCost ?? null,
    vatCost: api.vatCost ?? null,
    vatRate: api.vatRate ?? null,
    totalCost: api.totalCost ?? null,
    sessionId: api.sessionId ?? null,
    hasDetails: api.hasDetails,
    isVirtualMeter: api.isVirtualMeter,
  };
}
